/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  Check,
  ChevronRight,
  RotateCcw,
  AlertCircle,
} from 'lucide-react';
import { useToast } from 'src/hooks/use-toast';
import { useGenericOTP } from 'src/hooks/useGenericOTP';
import { OTPInput } from 'src/components/OTPVerification/OTPInput';
import { jwtDecode } from 'jwt-decode';
import {
  getUser,
  getRefreshToken,
  setToken,
  setRefreshToken,
  setUser,
} from 'src/utils/authStorage';
import { UserService } from 'src/services/api/UserService';
import Terms from 'src/pages/Terms/Terms';
import Privacy from 'src/pages/Privacy/Privacy';
import './CreateAccount.css';

/* ── Password strength ── */
function getPasswordStrength(pw) {
  if (!pw) return { level: 0, label: '', color: 'var(--text-faint)' };
  let sc = 0;
  if (pw.length >= 6) sc++;
  if (pw.length >= 10) sc++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) sc++;
  if (/[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw)) sc++;
  const lv = Math.min(sc, 4);
  const labels = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['var(--text-faint)', 'var(--destructive)', 'var(--warning)', '#5BA4D9', 'var(--success)'];
  return { level: lv, label: labels[lv], color: colors[lv] };
}

/* ── Google logo ── */
const GoogleLogo = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" style={{ width: size, height: size, flexShrink: 0 }}>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

/* ── Welcome screen steps data ── */
const WELCOME_STEPS = [
  {
    num: 1,
    title: 'Complete Your Profile',
    desc: 'Add your education, experience, skills, and certifications. Takes ~2 minutes.',
    numStyle: {
      background: 'var(--primary)',
      color: 'white',
      boxShadow: '0 2px 8px rgba(68,114,196,0.3)',
    },
    lineStyle: {
      background: 'linear-gradient(180deg,var(--primary),rgba(68,114,196,0.15))',
    },
  },
  {
    num: 2,
    title: 'Quick Assessment & Micro-Bootcamp',
    desc: '5-min skills check, then a bite-size bootcamp on AML/KYC basics.',
    numStyle: {
      background: 'var(--warning-light)',
      color: 'var(--warning)',
      border: '1.5px solid rgba(196,125,32,0.2)',
    },
    lineStyle: {
      background:
        'linear-gradient(180deg,rgba(196,125,32,0.2),rgba(46,139,106,0.1))',
    },
  },
  {
    num: 3,
    title: 'Get Matched & Launch Your Career',
    desc: "We'll match you to roles at leading banks & fintechs — interview and get hired.",
    numStyle: {
      background: 'var(--success-light)',
      color: 'var(--success)',
      border: '1.5px solid rgba(46,139,106,0.15)',
    },
    lineStyle: null,
  },
];

/* ── Spinner ── */
const Spinner = () => (
  <span
    className="la-spin"
    style={{
      display: 'inline-block',
      width: 16,
      height: 16,
      border: '2px solid rgba(255,255,255,0.3)',
      borderTopColor: 'white',
      borderRadius: '50%',
    }}
  />
);

/* ── Combined CreateAccount + Dashboard component ── */
export function CreateAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  /* ── Top-level screen ── */
  const [screen, setScreen] = useState('main'); // 'main' | 'welcome'

  /* ── Method selection ── */
  const [selectedMethod, setSelectedMethod] = useState(null); // 'google' | 'email' | null
  const [linkedMethod, setLinkedMethod] = useState(null); // 'Google' | 'Email' | null

  /* ── Loading ── */
  const [isLoading, setIsLoading] = useState(false);

  /* ── Email flow ── */
  const [emailStep, setEmailStep] = useState(1); // 1 | 2 | 3
  const [email, setEmail] = useState('');
  const [savedEmail, setSavedEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  /* ── Validation errors ── */
  const [emailErr, setEmailErr] = useState('');
  const [pwErr, setPwErr] = useState('');
  const [pw2Err, setPw2Err] = useState('');
  const [hasAcceptedWelcomeTerms, setHasAcceptedWelcomeTerms] = useState(false);
  const [welcomeTermsError, setWelcomeTermsError] = useState(false);
  const [legalModal, setLegalModal] = useState(null); // 'terms' | 'privacy' | null

  /* ── OTP hook for email linking ── */
  const emailOtpHook = useGenericOTP(
    email,
    'email',
    null,
    null,
    null,
    null,
    toast,
    'link'
  );

  /* ── On mount: skip linking if already linked ── */
  useEffect(() => {
    try {
      const user = getUser();
      if (user?.secondaryAuth && user.secondaryAuth !== 'none') {
        if (user?.ack === true) {
          navigate('/profile-dashboard', { replace: true });
        } else {
          setScreen('welcome');
        }
      }
    } catch (_) {}
  }, []);

  /* ── Check ack value when welcome screen is shown ── */
  useEffect(() => {
    if (screen !== 'welcome') return;
    try {
      const user = getUser();
      if (user?.ack === true) {
        navigate('/profile-dashboard', { replace: true });
      }
    } catch (_) {}
  }, [screen, navigate]);

  /* ── Block browser back navigation on welcome screen ── */
  useEffect(() => {
    if (screen !== 'welcome') return;
    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [screen]);

  /* ── Legal modal behavior (ESC + scroll lock) ── */
  useEffect(() => {
    if (!legalModal) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLegalModal(null);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [legalModal]);

  /* ── Refresh token & persist updated user info ── */
  const refreshAndSaveUser = useCallback(async () => {
    try {
      const storedRefreshToken = getRefreshToken();
      if (!storedRefreshToken) {
        console.warn('No refresh token available');
        return;
      }
      const res = await UserService.refreshToken(storedRefreshToken);
      const tokens = res?.data?.data?.tokens || res?.data?.tokens;
      if (!tokens?.access_token) {
        console.warn('No access token received from refresh');
        return;
      }
      setToken(tokens.access_token);
      if (tokens.refresh_token) setRefreshToken(tokens.refresh_token);
      try {
        const decoded = jwtDecode(tokens.access_token);
        const updatedUser = {
          cid: decoded?.cid,
          email: decoded?.email,
          firstName: decoded?.given_name,
          lastName: decoded?.family_name,
          emailVerified: decoded?.email_verified,
          roles: decoded?.realm_access?.roles,
          secAuth: decoded?.secAuth,
          secondaryAuth: decoded?.secondaryAuth,
          ack: decoded?.ack,
        };
        setUser(updatedUser);
        return updatedUser;
      } catch (decodeErr) {
        console.error('Failed to decode refreshed token:', decodeErr);
        return null;
      }
    } catch (err) {
      console.error('Failed to refresh token after linking:', err);
      return null;
    }
  }, []);

  /* ── Advance step 1 → 2 once OTP is confirmed sent ── */
  useEffect(() => {
    if (emailOtpHook.isOTPSent && emailStep === 1 && savedEmail) {
      setEmailStep(2);
    }
  }, [emailOtpHook.isOTPSent, emailStep, savedEmail]);

  /* ── After OTP verified: refresh user, then route to welcome/dashboard ── */
  useEffect(() => {
    if (emailOtpHook.verified && emailStep === 3) {
      refreshAndSaveUser()
        .then((updatedUser) => {
          if (updatedUser) {
            setLinkedMethod('Email');
            toast({ message: 'Email linked', variant: 'success' });
            if (updatedUser?.ack === true) {
              navigate('/profile-dashboard', { replace: true });
            } else {
              setScreen('welcome');
            }
          } else {
            toast({
              variant: 'destructive',
              message:
                'Account linked but failed to refresh user info. Please refresh the page.',
            });
          }
        })
        .catch((err) => {
          console.error('Error refreshing user after email linking:', err);
          toast({
            variant: 'destructive',
            message:
              'Account linked but failed to refresh user info. Please refresh the page.',
          });
        });
    }
  }, [emailOtpHook.verified, emailStep, refreshAndSaveUser, toast, navigate]);

  /* ── Handle Google OAuth callback query params ── */
  useEffect(() => {
    if (!location?.search) return;
    const params = new URLSearchParams(location.search);
    const linked = params.get('linked');
    const status = params.get('status');
    const emailParam = params.get('email');
    if (linked === 'google') {
      if (status === 'success') {
        setSelectedMethod('google');
        refreshAndSaveUser()
          .then((updatedUser) => {
            if (updatedUser) {
              setLinkedMethod('Google');
              toast({
                message: 'Google linked',
                variant: 'success',
              });
              if (updatedUser?.ack === true) {
                navigate('/profile-dashboard', { replace: true });
              } else {
                setScreen('welcome');
              }
            } else {
              toast({
                variant: 'destructive',
                message:
                  'Account linked but failed to refresh user info. Please refresh the page.',
              });
            }
          })
          .catch((err) => {
            console.error('Error refreshing user after Google linking:', err);
            toast({
              variant: 'destructive',
              message:
                'Account linked but failed to refresh user info. Please refresh the page.',
            });
          });
      } else if (status && status !== 'success') {
        toast({
          variant: 'destructive',
          message: 'Linking failed. Please retry.',
        });
      }
    }
  }, [location, toast, refreshAndSaveUser, navigate]);

  /* ── Helpers ── */
  const resetEmailFlow = useCallback(() => {
    setEmailStep(1);
    setSavedEmail('');
    setPassword('');
    setConfirmPassword('');
    setEmailErr('');
    setPwErr('');
    setPw2Err('');
  }, []);

  const handleSelectMethod = (method) => {
    if (linkedMethod) return;
    setSelectedMethod(method);
    if (method === 'email') resetEmailFlow();
  };

  const handleStartOver = useCallback(() => {
    resetEmailFlow();
    setEmail('');
    setSelectedMethod(null);
  }, [resetEmailFlow]);

  /* ── Email Step 1: Send OTP ── */
  const handleSendCode = async () => {
    setEmailErr('');
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!email.trim()) {
      setEmailErr('Email is required');
      return;
    }
    if (!re.test(email)) {
      setEmailErr('Enter a valid email address');
      return;
    }
    setSavedEmail(email);
    await emailOtpHook.handleSendOTP('SEND');
    toast({ message: `Code sent to ${email.trim()}` });
  };

  /* ── Email Step 2: Validate passwords, advance to OTP ── */
  const handleAdvanceToOTP = () => {
    setPwErr('');
    setPw2Err('');
    let ok = true;
    if (password.length < 6) {
      setPwErr('Password must be at least 6 characters');
      ok = false;
    }
    if (password !== confirmPassword) {
      setPw2Err('Passwords do not match');
      ok = false;
    }
    if (!ok) return;
    setEmailStep(3);
  };

  /* ── Email Step 3: Submit OTP + password for verification ── */
  const handleSubmitOTP = async () => {
    if (emailOtpHook.currentOTP.length !== 4) {
      toast({
        variant: 'destructive',
        message: 'Please enter the 4-digit OTP',
      });
      return;
    }
    await emailOtpHook.verifyOTPWithData({ password, confirmPassword });
  };

  /* ── Google linking (redirects to OAuth) ── */
  const handleGoogleLink = async () => {
    setIsLoading(true);
    try {
      const { UserService: UserApi } =
        await import('src/services/api/UserService');
      const res = await UserApi.initiateGoogleSecondaryLink();
      const authUrl =
        res?.data?.auth_url || res?.data?.data?.auth_url || res?.auth_url;
      if (authUrl) {
        try {
          localStorage.setItem('pendingSecondaryLink', 'google');
          localStorage.setItem('pendingSecondaryLinkReturn', '/create-link');
        } catch (_) {}
        window.location.assign(authUrl);
        return;
      }
      throw new Error('Failed to get Google authorization URL');
    } catch (error) {
      console.error('Google link error:', error);
      if (error?.response?.status === 409) {
        const method = error?.response?.data?.method || 'this method';
        toast({
          variant: 'destructive',
          message: `You have already configured ${method}.`,
        });
        return;
      }
      toast({
        variant: 'destructive',
        message: 'Failed to link Google account. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Skip linking ── */
  const handleSkip = () => {
    toast({ message: 'You can link a backup method later in Settings.' });
    try {
      const user = getUser();
      if (user?.ack === true) {
        navigate('/profile-dashboard');
      } else {
        setScreen('welcome');
      }
    } catch (_) {
      setScreen('welcome');
    }
  };

  /* ── Welcome CTA ── */
  const handleCompleteProfile = async () => {
    if (!hasAcceptedWelcomeTerms) {
      setWelcomeTermsError(true);
      toast({
        message: 'Please agree to the Terms & Conditions and Privacy Policy.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsLoading(true);
      await UserService.acknowledgeIntro();
      // Refresh token to get updated user info with new ack value
      await refreshAndSaveUser();
      navigate('/profile-dashboard');
    } catch (error) {
      console.error('Failed to acknowledge intro:', error);
      toast({
        message: 'Something went wrong. Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* ── Computed ── */
  const pwStrength = getPasswordStrength(password);
  const isLinked = (method) => linkedMethod?.toLowerCase() === method;

  const methodOptClass = (method) => {
    let cls = 'la-method-opt';
    if (isLinked(method)) cls += ' la-linked';
    else if (selectedMethod === method) cls += ' la-selected';
    else if (linkedMethod) cls += ' la-disabled';
    return cls;
  };

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <>
      <div
        className={`legal-overlay${legalModal ? ' show' : ''}`}
        role="presentation"
        onMouseDown={() => setLegalModal(null)}
      />
      <div
        className={`legal-modal${legalModal ? ' show' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={
          legalModal === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'
        }
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="legal-modal-head">
          <h2>
            {legalModal === 'terms' ? 'Terms & Conditions' : 'Privacy Policy'}
          </h2>
          <button
            type="button"
            className="legal-close"
            onClick={() => setLegalModal(null)}
            aria-label="Close"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="legal-body">
          {legalModal === 'terms' ? (
            <Terms embedded />
          ) : legalModal === 'privacy' ? (
            <Privacy embedded />
          ) : null}
        </div>
      </div>

      {/* ══ MAIN SCREEN ══ */}
      {screen === 'main' && (
        <div style={{ minHeight: '100%', background: 'var(--bg)' }}>
          {/* Skip button — fixed on the app's existing header */}
          <button onClick={handleSkip} className="la-skip-fixed">
            Skip <span aria-hidden>→</span>
          </button>

          {/* PAGE */}
          <div className="la-page">
            {/* Page header */}
            <div
              className={`la-page-header la-anim-fade-up${selectedMethod ? ' has-sel' : ''}`}
            >
              <h1>Add a backup sign-in</h1>
              <p>
                You signed up with <strong>Phone-OTP</strong>. Link one more
                method for easier recovery.
              </p>
            </div>

            {/* Panels */}
            <div className={`la-panels${selectedMethod ? ' has-sel' : ''}`}>
              {/* ── LEFT PANEL ── */}
              <div className="la-panel-left">
                {/* Google option */}
                <div
                  className={methodOptClass('google')}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectMethod('google')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectMethod('google');
                    }
                  }}
                >
                  <div className="la-linked-check">
                    <Check style={{ width: 12, height: 12 }} strokeWidth={3} />
                  </div>
                  <div className="la-method-radio">
                    <div className="la-method-radio-dot" />
                  </div>
                  <div className="la-method-icon g-bg">
                    <GoogleLogo size={18} />
                  </div>
                  <div className="la-method-text">
                    <div className="la-method-name">
                      Google
                      {isLinked('google') && (
                        <span
                          style={{
                            fontWeight: 500,
                            fontSize: '0.72rem',
                            opacity: 0.8,
                            marginLeft: 4,
                          }}
                        >
                          · Linked
                        </span>
                      )}
                    </div>
                    <div className="la-method-desc">
                      {isLinked('google')
                        ? 'Linked successfully'
                        : 'One-tap sign in with your Google account'}
                    </div>
                  </div>
                  {!isLinked('google') && (
                    <div className="la-method-arrow">
                      <ChevronRight style={{ width: 16, height: 16 }} />
                    </div>
                  )}
                </div>

                {/* Email option */}
                <div
                  className={methodOptClass('email')}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectMethod('email')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSelectMethod('email');
                    }
                  }}
                >
                  <div className="la-linked-check">
                    <Check style={{ width: 12, height: 12 }} strokeWidth={3} />
                  </div>
                  <div className="la-method-radio">
                    <div className="la-method-radio-dot" />
                  </div>
                  <div className="la-method-icon e-bg">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ width: 18, height: 18 }}
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div className="la-method-text">
                    <div className="la-method-name">
                      Email
                      {isLinked('email') && (
                        <span
                          style={{
                            fontWeight: 500,
                            fontSize: '0.72rem',
                            opacity: 0.8,
                            marginLeft: 4,
                          }}
                        >
                          · Linked
                        </span>
                      )}
                    </div>
                    <div className="la-method-desc">
                      {isLinked('email')
                        ? 'Linked successfully'
                        : 'Link with email, password & verification'}
                    </div>
                  </div>
                  {!isLinked('email') && (
                    <div className="la-method-arrow">
                      <ChevronRight style={{ width: 16, height: 16 }} />
                    </div>
                  )}
                </div>
              </div>

              {/* ── RIGHT PANEL ── */}
              {selectedMethod ? (
                <div className="la-panel-right la-anim-fade-up">
                  {/* Google form */}
                  {selectedMethod === 'google' && (
                    <div className="la-form-inner la-anim-scale-in">
                      <p
                        style={{
                          fontSize: '0.82rem',
                          color: 'var(--text-muted)',
                          lineHeight: 1.55,
                          marginBottom: 16,
                        }}
                      >
                        We'll attach your Google identity to this account. You
                        can unlink later in Settings.
                      </p>
                      <button
                        className="la-btn-primary"
                        onClick={handleGoogleLink}
                        disabled={isLoading}
                      >
                        <GoogleLogo size={17} />
                        {isLoading ? (
                          <>
                            <Spinner /> Redirecting…
                          </>
                        ) : (
                          'Link Google Account'
                        )}
                      </button>
                    </div>
                  )}

                  {/* Email form */}
                  {selectedMethod === 'email' && (
                    <div className="la-form-inner">
                      {/* Stepper */}
                      <div className="la-esteps">
                        {[
                          { n: 1, label: 'Email' },
                          { n: 2, label: 'Password' },
                          { n: 3, label: 'Verify' },
                        ].map(({ n, label }, idx) => (
                          <React.Fragment key={n}>
                            <div
                              className={`la-estep${n === emailStep ? ' la-active' : n < emailStep ? ' la-done' : ''}`}
                            >
                              <div className="la-estep-num">
                                {n < emailStep ? (
                                  <Check
                                    style={{ width: 10, height: 10 }}
                                    strokeWidth={3}
                                  />
                                ) : (
                                  n
                                )}
                              </div>
                              {label}
                            </div>
                            {idx < 2 && (
                              <div
                                className={`la-estep-line${n < emailStep ? ' la-done' : ''}`}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </div>

                      {/* ── Step 1: Email ── */}
                      {emailStep === 1 && (
                        <div
                          className="la-anim-scale-in"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                          }}
                        >
                          <div className="la-ff">
                            <label className="la-ff-label">Email address</label>
                            <input
                              type="email"
                              className={`la-ff-input${emailErr ? ' la-err' : ''}`}
                              placeholder="Enter your email address"
                              autoComplete="email"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailErr('');
                              }}
                            />
                            {emailErr && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginTop: 5,
                                  fontSize: '0.74rem',
                                  color: 'var(--destructive)',
                                  fontWeight: 500,
                                }}
                              >
                                <AlertCircle
                                  style={{
                                    width: 12,
                                    height: 12,
                                    flexShrink: 0,
                                  }}
                                />
                                {emailErr}
                              </div>
                            )}
                          </div>
                          <button
                            className="la-btn-primary"
                            onClick={handleSendCode}
                            disabled={emailOtpHook.isOTPLoading}
                          >
                            {emailOtpHook.isOTPLoading ? (
                              <>
                                <Spinner /> Sending…
                              </>
                            ) : (
                              'Send code'
                            )}
                          </button>
                          <button
                            onClick={handleStartOver}
                            className="la-btn-ghost"
                          >
                            <RotateCcw style={{ width: 14, height: 14 }} />
                            Start over
                          </button>
                        </div>
                      )}

                      {/* ── Step 2: Password ── */}
                      {emailStep === 2 && (
                        <div
                          className="la-anim-scale-in"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                          }}
                        >
                          {/* Sent banner */}
                          <div className="la-sent-row">
                            <Check
                              style={{ width: 15, height: 15, flexShrink: 0 }}
                            />
                            <span>
                              Code sent to <strong>{savedEmail}</strong>
                            </span>
                            <button
                              className="la-sent-change"
                              onClick={resetEmailFlow}
                            >
                              Change
                            </button>
                          </div>

                          {/* Password */}
                          <div className="la-ff">
                            <label className="la-ff-label">
                              Create password
                            </label>
                            <div className="la-pw-wrap">
                              <input
                                type={showPw1 ? 'text' : 'password'}
                                className={`la-ff-input${pwErr ? ' la-err' : ''}`}
                                placeholder="Min 6 characters"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => {
                                  setPassword(e.target.value);
                                  setPwErr('');
                                }}
                              />
                              <button
                                type="button"
                                className="la-pw-toggle"
                                onClick={() => setShowPw1(!showPw1)}
                                tabIndex={-1}
                              >
                                {showPw1 ? (
                                  <EyeOff style={{ width: 18, height: 18 }} />
                                ) : (
                                  <Eye style={{ width: 18, height: 18 }} />
                                )}
                              </button>
                            </div>
                            {password && (
                              <div style={{ marginTop: 8 }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 3,
                                    marginBottom: 4,
                                  }}
                                >
                                  {[1, 2, 3, 4].map((i) => (
                                    <div
                                      key={i}
                                      style={{
                                        flex: 1,
                                        height: 3,
                                        borderRadius: 2,
                                        background:
                                          i <= pwStrength.level
                                            ? pwStrength.color
                                            : 'var(--border-light)',
                                        transition: 'background 0.3s ease',
                                      }}
                                    />
                                  ))}
                                </div>
                                <span
                                  style={{
                                    fontSize: '0.68rem',
                                    fontWeight: 600,
                                    color: pwStrength.color,
                                  }}
                                >
                                  {pwStrength.label}
                                </span>
                              </div>
                            )}
                            {pwErr && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginTop: 5,
                                  fontSize: '0.74rem',
                                  color: 'var(--destructive)',
                                  fontWeight: 500,
                                }}
                              >
                                <AlertCircle
                                  style={{
                                    width: 12,
                                    height: 12,
                                    flexShrink: 0,
                                  }}
                                />
                                {pwErr}
                              </div>
                            )}
                          </div>

                          {/* Confirm password */}
                          <div className="la-ff">
                            <label className="la-ff-label">
                              Confirm password
                            </label>
                            <div className="la-pw-wrap">
                              <input
                                type={showPw2 ? 'text' : 'password'}
                                className={`la-ff-input${pw2Err ? ' la-err' : ''}`}
                                placeholder="Re-enter password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => {
                                  setConfirmPassword(e.target.value);
                                  setPw2Err('');
                                }}
                              />
                              <button
                                type="button"
                                className="la-pw-toggle"
                                onClick={() => setShowPw2(!showPw2)}
                                tabIndex={-1}
                              >
                                {showPw2 ? (
                                  <EyeOff style={{ width: 18, height: 18 }} />
                                ) : (
                                  <Eye style={{ width: 18, height: 18 }} />
                                )}
                              </button>
                            </div>
                            {pw2Err && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  marginTop: 5,
                                  fontSize: '0.74rem',
                                  color: 'var(--destructive)',
                                  fontWeight: 500,
                                }}
                              >
                                <AlertCircle
                                  style={{
                                    width: 12,
                                    height: 12,
                                    flexShrink: 0,
                                  }}
                                />
                                {pw2Err}
                              </div>
                            )}
                          </div>

                          <button
                            className="la-btn-primary"
                            onClick={handleAdvanceToOTP}
                          >
                            Continue
                          </button>
                          <button
                            onClick={handleStartOver}
                            className="la-btn-ghost"
                          >
                            <RotateCcw style={{ width: 14, height: 14 }} />
                            Start over
                          </button>
                        </div>
                      )}

                      {/* ── Step 3: OTP verify ── */}
                      {emailStep === 3 && (
                        <div
                          className="la-anim-scale-in"
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                          }}
                        >
                          {/* Confirmation banner */}
                          <div className="la-sent-row">
                            <Check
                              style={{ width: 15, height: 15, flexShrink: 0 }}
                            />
                            <span>
                              Verify the code sent to{' '}
                              <strong>{savedEmail}</strong>
                            </span>
                          </div>

                          {/* OTP boxes */}
                          <OTPInput
                            length={4}
                            onChange={emailOtpHook.handleVerifyOTP}
                            disabled={emailOtpHook.isVerifyingOTP}
                            otpError={emailOtpHook.otpError}
                          />

                          {emailOtpHook.otpError && (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: '0.74rem',
                                color: 'var(--destructive)',
                                fontWeight: 500,
                              }}
                            >
                              <AlertCircle
                                style={{ width: 12, height: 12, flexShrink: 0 }}
                              />
                              {emailOtpHook.otpError}
                            </div>
                          )}

                          {/* Resend */}
                          <div className="la-resend-row">
                            <span>Didn't get it?</span>
                            <button
                              className="la-resend-btn"
                              disabled={!emailOtpHook.canResend}
                              onClick={() =>
                                emailOtpHook.handleSendOTP('RESEND')
                              }
                            >
                              {emailOtpHook.canResend
                                ? 'Resend code'
                                : `Resend in ${emailOtpHook.resendTimeout}s`}
                            </button>
                          </div>

                          <button
                            className="la-btn-primary"
                            onClick={handleSubmitOTP}
                            disabled={
                              emailOtpHook.isVerifyingOTP ||
                              emailOtpHook.currentOTP.length < 4
                            }
                          >
                            {emailOtpHook.isVerifyingOTP ? (
                              <>
                                <Spinner /> Verifying…
                              </>
                            ) : (
                              'Verify and Link'
                            )}
                          </button>
                          <button
                            onClick={handleStartOver}
                            className="la-btn-ghost"
                          >
                            <RotateCcw style={{ width: 14, height: 14 }} />
                            Start over
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Placeholder when nothing is selected */
                <></>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ══ WELCOME SCREEN ══ */}
      {screen === 'welcome' && (
        <div
          style={{
            minHeight: '100%',
            background: 'var(--bg)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '48px 28px 60px',
          }}
        >
          <div
            className="la-anim-fade-up"
            style={{ maxWidth: 620, width: '100%', textAlign: 'left' }}
          >
            <h1
              style={{
                fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
                fontSize: '1.6rem',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: 'var(--text)',
                marginBottom: 6,
                lineHeight: 1.2,
              }}
            >
              Welcome to Ahedge People Services
            </h1>
            <p
              style={{
                fontSize: '0.88rem',
                color: 'var(--text-muted)',
                lineHeight: 1.6,
                marginBottom: 32,
              }}
            >
              Three quick steps to launch your career in Risk &amp; Compliance.
            </p>

            {/* Steps */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                marginBottom: 36,
              }}
            >
              {WELCOME_STEPS.map((step, idx) => (
                <div
                  key={step.num}
                  className="la-w-step"
                  style={{
                    animation: `la-fadeUp 0.4s cubic-bezier(0.16,1,0.3,1) ${0.1 + idx * 0.1}s both`,
                  }}
                >
                  {/* Rail */}
                  <div className="la-w-step-rail">
                    <div className="la-w-step-num" style={step.numStyle}>
                      {step.num}
                    </div>
                    {idx < WELCOME_STEPS.length - 1 && (
                      <div className="la-w-step-line" style={step.lineStyle} />
                    )}
                  </div>
                  {/* Content */}
                  <div className="la-w-step-body">
                    <div className="la-w-step-title">{step.title}</div>
                    <div className="la-w-step-desc">{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* T&C checkbox (matches link-account-improved.html CSS) */}
            <div
              className="tc-wrapper"
              style={{
                animation: 'fadeUp 0.4s var(--ease-out) 0.4s both',
              }}
            >
              <label className="tc-label" htmlFor="tcCheck">
                <input
                  id="tcCheck"
                  type="checkbox"
                  checked={hasAcceptedWelcomeTerms}
                  onChange={(e) => {
                    setHasAcceptedWelcomeTerms(e.target.checked);
                    setWelcomeTermsError(false);
                  }}
                />
                <span
                  className={`tc-box${welcomeTermsError ? ' has-error' : ''}`}
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className="tc-text">
                  I agree to the{' '}
                  <button
                    type="button"
                    className="tc-link"
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      setLegalModal('terms');
                    }}
                  >
                    Terms &amp; Conditions
                  </button>{' '}
                  and{' '}
                  <button
                    type="button"
                    className="tc-link"
                    onClick={(ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      setLegalModal('privacy');
                    }}
                  >
                    Privacy Policy
                  </button>
                </span>
              </label>
              <div className={`tc-error${welcomeTermsError ? ' show' : ''}`}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>
                  You must accept the Terms &amp; Conditions to proceed
                </span>
              </div>
            </div>

            <button
              onClick={handleCompleteProfile}
              disabled={isLoading || !hasAcceptedWelcomeTerms}
              className={`welcome-cta${isLoading || !hasAcceptedWelcomeTerms ? ' disabled' : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '14px 28px',
                borderRadius: 8,
                border: 'none',
                cursor:
                  isLoading || !hasAcceptedWelcomeTerms
                    ? 'not-allowed'
                    : 'pointer',
                fontFamily: 'inherit',
                fontSize: '0.9rem',
                fontWeight: 700,
                background: 'var(--primary)',
                color: 'white',
                boxShadow: '0 2px 10px rgba(68,114,196,0.3)',
                transition: 'all 0.15s ease',
                opacity: isLoading || !hasAcceptedWelcomeTerms ? 0.7 : 1,
              }}
            >
              {isLoading ? (
                <>
                  <Spinner />
                  Loading…
                </>
              ) : (
                <>
                  Let&apos;s get started
                  <ChevronRight style={{ width: 16, height: 16 }} />
                </>
              )}
            </button>

            <p
              style={{
                fontSize: '0.78rem',
                color: 'var(--text-faint)',
                marginTop: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{ width: 14, height: 14, flexShrink: 0 }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Takes ~2 minutes.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
